import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Edit,
  Trash2,
  Plus,
  Type,
  Search,
  Eye,
} from "lucide-react";
import Button from "components/ui/Button";
import {
  useAddOptionMutation,
  useDeleteOptionMutation,
  useGetOptionQuery,
  useUpdateOptionMutation,
} from "apis/communityTriggerOptions";
import { FiEye } from "react-icons/fi";
import { MdAdd } from "react-icons/md";
import Modal from "components/ui/Modal";
import { useForm } from "react-hook-form";

const CommunityTriggerOptions = () => {
  const [groupedData, setGroupedData] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingOption, setEditingOption] = useState(null);
  const [viewingTemplates, setViewingTemplates] = useState({});
  const [templateSearchTerms, setTemplateSearchTerms] = useState({});

  // Delete modal state
  const [isOptionDeleteOpen, setIsOptionDeleteOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);

  // Template modal state
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [currentOption, setCurrentOption] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Template delete modal state
  const [isTemplateDeleteOpen, setIsTemplateDeleteOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Template view modal state
  const [isTemplateViewOpen, setIsTemplateViewOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);

  const { mutate: addOption } = useAddOptionMutation();
  const { data: optionsData, isLoading, isError, error } = useGetOptionQuery();
  const { mutate: updateOption } = useUpdateOptionMutation();
  const { mutate: deleteOption } = useDeleteOptionMutation();

  // React Hook Form for template
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm();

  // Define the four categories
  const categories = ["Type", "Vessel", "Receiver", "Delivery"];

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleTemplateView = (optionId) => {
    setViewingTemplates((prev) => ({
      ...prev,
      [optionId]: !prev[optionId],
    }));

    // Clear search term when closing template view
    if (viewingTemplates[optionId]) {
      setTemplateSearchTerms((prev) => ({
        ...prev,
        [optionId]: "",
      }));
    }
  };

  const handleTemplateSearch = (optionId, searchTerm) => {
    setTemplateSearchTerms((prev) => ({
      ...prev,
      [optionId]: searchTerm,
    }));
  };

  const filterTemplates = (templates, searchTerm) => {
    if (!searchTerm) return templates;

    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (template.prompt &&
          template.prompt.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (template.rule &&
          template.rule.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  useEffect(() => {
    if (optionsData?.length > 0) {
      // Group data by category
      const grouped = optionsData.reduce((acc, item) => {
        const category = item.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {});

      setGroupedData(grouped);
    }
  }, [optionsData]);

  const editOption = (category, optionIndex, field, value) => {
    const newData = { ...groupedData };
    newData[category][optionIndex][field] = value;
    setGroupedData(newData);
  };

  const addNewOption = (category) => {
    const newOptionData = {
      name: "New Option",
      category: category,
      templates: [],
    };

    // Add time field for Vessel category
    if (category === "Vessel") {
      newOptionData.time = 0;
    }

    addOption(newOptionData);
  };

  const handleOptionSave = (option) => {
    const updateData = {
      id: option._id,
      name: option.name,
      category: option.category,
    };

    // Include time field for Vessel category
    if (option.category === "Vessel") {
      updateData.time = option.time;
    }

    updateOption(updateData);
    setEditingOption(null);
  };

  const handleTimeChange = (category, optionIndex, value) => {
    // Allow decimal values
    const numValue = parseFloat(value);
    editOption(category, optionIndex, "time", numValue);
  };

  // Option delete handlers
  const handleDeleteOption = (option) => {
    setOptionToDelete(option);
    setIsOptionDeleteOpen(true);
  };

  const handleDelete = () => {
    if (optionToDelete) {
      deleteOption(optionToDelete._id);
      setIsOptionDeleteOpen(false);
      setOptionToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsOptionDeleteOpen(false);
    setOptionToDelete(null);
  };

  // Template modal handlers
  const handleAddTemplate = (option) => {
    setCurrentOption(option);
    setEditingTemplate(null);
    reset();
    setIsTemplateModalOpen(true);
  };

  const handleEditTemplate = (option, template) => {
    setCurrentOption(option);
    setEditingTemplate(template);
    setValue("name", template.name);
    setValue("message", template.message);
    setValue("prompt", template.prompt || "");
    setValue("rule", template.rule || "");
    setIsTemplateModalOpen(true);
  };

  const handleViewTemplate = (template) => {
    setViewingTemplate(template);
    setIsTemplateViewOpen(true);
  };

  const handleCloseTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setCurrentOption(null);
    setEditingTemplate(null);
    reset();
  };

  const handleCloseTemplateView = () => {
    setIsTemplateViewOpen(false);
    setViewingTemplate(null);
  };

  const onSubmitTemplate = (data) => {
    if (!currentOption) return;

    const updatedTemplates = [...(currentOption.templates || [])];

    if (editingTemplate) {
      // Edit existing template
      const templateIndex = updatedTemplates.findIndex(
        (t) => t._id === editingTemplate._id
      );
      if (templateIndex !== -1) {
        updatedTemplates[templateIndex] = {
          ...editingTemplate,
          name: data.name,
          message: data.message,
          prompt: data.prompt,
          rule: data.rule,
        };
      }
    } else {
      // Add new template - let backend generate _id
      const newTemplate = {
        name: data.name,
        message: data.message,
        prompt: data.prompt,
        rule: data.rule,
      };
      updatedTemplates.push(newTemplate);
    }

    // Update the option with new templates
    const updateData = {
      id: currentOption._id,
      name: currentOption.name,
      category: currentOption.category,
      templates: updatedTemplates,
    };

    // Include time field for Vessel category
    if (currentOption.category === "Vessel") {
      updateData.time = currentOption.time;
    }

    updateOption(updateData);

    handleCloseTemplateModal();
  };

  // Template delete handlers
  const handleDeleteTemplate = (option, template) => {
    setCurrentOption(option);
    setTemplateToDelete(template);
    setIsTemplateDeleteOpen(true);
  };

  const handleConfirmDeleteTemplate = () => {
    if (!currentOption || !templateToDelete) return;

    const updatedTemplates = currentOption.templates.filter(
      (t) => t._id !== templateToDelete._id
    );

    const updateData = {
      id: currentOption._id,
      name: currentOption.name,
      category: currentOption.category,
      templates: updatedTemplates,
    };

    // Include time field for Vessel category
    if (currentOption.category === "Vessel") {
      updateData.time = currentOption.time;
    }

    updateOption(updateData);

    setIsTemplateDeleteOpen(false);
    setCurrentOption(null);
    setTemplateToDelete(null);
  };

  const handleCancelDeleteTemplate = () => {
    setIsTemplateDeleteOpen(false);
    setCurrentOption(null);
    setTemplateToDelete(null);
  };

  return (
    <div className="container mx-auto p-4">
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error: {error?.message}</div>}

      {categories.map((category) => {
        const categoryOptions = groupedData[category] || [];

        return (
          <div
            key={category}
            className="mb-4 border border-mediumgray rounded-lg overflow-hidden"
          >
            <div className="bg-lightgray p-3 flex justify-between items-center">
              <div className="flex items-center flex-grow">
                <button onClick={() => toggleCategory(category)}>
                  {expandedCategories[category] ? (
                    <ChevronDown className="text-black" size={20} />
                  ) : (
                    <ChevronRight className="text-black" size={20} />
                  )}
                </button>

                <span className="font-semibold flex-grow ml-2">{category}</span>
              </div>
            </div>

            {expandedCategories[category] && (
              <div className="p-3">
                {categoryOptions.map((option, optionIndex) => (
                  <div key={option._id} className="mb-4">
                    <div className="flex flex-col py-2">
                      <div className="flex justify-between items-center">
                        {editingOption === option._id ? (
                          <input
                            type="text"
                            value={option.name}
                            onChange={(e) =>
                              editOption(
                                category,
                                optionIndex,
                                "name",
                                e.target.value
                              )
                            }
                            onBlur={() => handleOptionSave(option)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleOptionSave(option);
                              }
                            }}
                            className="form-input flex-grow border border-mediumgray mr-2"
                            autoFocus
                          />
                        ) : (
                          <span className="flex-grow">{option.name}</span>
                        )}

                        <div className="flex">
                          <button
                            onClick={() => setEditingOption(option._id)}
                            className="mr-2"
                          >
                            <Edit className="text-black" size={16} />
                          </button>
                          <button onClick={() => handleDeleteOption(option)}>
                            <Trash2 className="text-black" size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Time input field for Vessel category */}
                      {category === "Vessel" && (
                        <div className="flex items-center mt-2 gap-2">
                          <span className="font-medium ">Time:</span>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={option.time}
                            onChange={(e) =>
                              handleTimeChange(
                                category,
                                optionIndex,
                                e.target.value
                              )
                            }
                            onBlur={() => handleOptionSave(option)}
                            className="!text-sm px-2 py-1 rounded w-16 border border-mediumgray"
                          />
                          <span className="text-sm">hours</span>
                        </div>
                      )}

                      <div className="flex mt-2 gap-2">
                        <Button
                          onClick={() => toggleTemplateView(option._id)}
                          className="outline-button flex items-center px-2 py-1.5 gap-1"
                        >
                          <FiEye size={12} />
                          <span className="!text-xs">
                            View Templates ({option.templates?.length || 0})
                          </span>
                        </Button>

                        <Button
                          onClick={() => handleAddTemplate(option)}
                          className="outline-button flex items-center px-2 py-1 gap-1"
                        >
                          <MdAdd size={12} />
                          <span className="!text-xs">Add Template</span>
                        </Button>
                      </div>
                    </div>

                    {/* Template List */}
                    {viewingTemplates[option._id] && (
                      <div className="ml-4 mt-2 border-l-2 border-mediumgray pl-4">
                        {/* Search Bar */}
                        <div className="mb-2">
                          <div className="relative">
                            <Search
                              className="absolute left-2 top-1/2 -translate-y-1/2 text-darkgray"
                              size={16}
                            />

                            <input
                              type="text"
                              placeholder="Search templates..."
                              value={templateSearchTerms[option._id] || ""}
                              onChange={(e) =>
                                handleTemplateSearch(option._id, e.target.value)
                              }
                              className="form-input w-full pl-8 border border-mediumgray"
                            />
                          </div>
                        </div>

                        {option.templates && option.templates.length > 0 ? (
                          (() => {
                            const filteredTemplates = filterTemplates(
                              option.templates,
                              templateSearchTerms[option._id] || ""
                            );

                            return filteredTemplates.length > 0 ? (
                              filteredTemplates.map((template) => (
                                <div
                                  key={template._id}
                                  className="flex justify-between items-center py-2 border-b border-lightgray"
                                >
                                  <span className="flex-grow">
                                    {template.name}
                                  </span>
                                  <div className="flex">
                                    <Button
                                      onClick={() =>
                                        handleViewTemplate(template)
                                      }
                                      className="outline-button flex gap-1 px-2 py-1 text-xs mr-2"
                                    >
                                      <Eye size={14} />
                                      View
                                    </Button>
                                    <button
                                      onClick={() =>
                                        handleEditTemplate(option, template)
                                      }
                                      className="mr-2"
                                    >
                                      <Edit size={14} />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteTemplate(option, template)
                                      }
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="py-2">
                                No templates match your search
                              </p>
                            );
                          })()
                        ) : (
                          <p className="py-2">No templates found</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                <Button
                  onClick={() => addNewOption(category)}
                  className="mt-2 flex gap-2 items-center gray-button"
                >
                  <Plus size={14} /> Add Option
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {/* Delete Option Confirmation Modal */}
      <Modal
        isOpen={isOptionDeleteOpen}
        onClose={handleCancelDelete}
        title="Delete Confirmation"
      >
        <div>
          <p>
            Are you sure you want to delete this option? This will also delete
            all the templates associated with it.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <Button onClick={handleCancelDelete} className="gray-button">
              Cancel
            </Button>
            <Button onClick={handleDelete} className="black-button">
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Template Modal */}
      <Modal
        isOpen={isTemplateModalOpen}
        onClose={handleCloseTemplateModal}
        title={editingTemplate ? "Edit Template" : "Add Template"}
      >
        <form onSubmit={handleSubmit(onSubmitTemplate)}>
          <div className="mb-4">
            <label htmlFor="name" className="form-label">
              Name
            </label>
            <input
              type="text"
              id="name"
              {...register("name", { required: "Name is required" })}
              className={`form-input border ${
                errors.name ? "border-red-500" : "border-mediumgray"
              }`}
            />
            {errors.name && (
              <span className="form-error-message">{errors.name.message}</span>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="message" className="form-label">
              Message
            </label>
            <textarea
              id="message"
              rows="4"
              {...register("message", { required: "Message is required" })}
              className={`form-textarea border ${
                errors.message ? "border-red-500" : "border-mediumgray"
              }`}
            />
            {errors.message && (
              <span className="form-error-message">
                {errors.message.message}
              </span>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="rule" className="form-label">
              Rule
            </label>
            <textarea
              id="rule"
              rows="2"
              {...register("rule")}
              className="form-textarea border border-mediumgray"
              placeholder="Enter rules for this template..."
            />
          </div>

          <div className="mb-4">
            <label htmlFor="prompt" className="form-label">
              Prompt (optional)
            </label>
            <textarea
              id="prompt"
              rows="2"
              {...register("prompt")}
              className="form-textarea border border-mediumgray"
            />
          </div>

          <div className="w-full flex gap-2 justify-end">
            <Button
              type="button"
              onClick={handleCloseTemplateModal}
              className="gray-button"
            >
              Cancel
            </Button>
            <Button type="submit" className="black-button">
              {editingTemplate ? "Update" : "Save"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Template View Modal */}
      <Modal
        isOpen={isTemplateViewOpen}
        onClose={handleCloseTemplateView}
        title="Template Details"
      >
        {viewingTemplate && (
          <div className="space-y-4">
            <div>
              <h4 className="mb-2">{viewingTemplate.name}</h4>
            </div>

            <div>
              <label className="form-label font-medium">Message:</label>
              <div className="bg-lightergray p-3 rounded border border-mediumgray">
                <pre className="whitespace-pre-wrap">
                  {viewingTemplate.message}
                </pre>
              </div>
            </div>

            {viewingTemplate.rule && (
              <div>
                <label className="form-label font-medium">Rule:</label>
                <div className="bg-indigo-50 p-3 rounded border border-indigo-200">
                  <p className="whitespace-pre-wrap">{viewingTemplate.rule}</p>
                </div>
              </div>
            )}

            {viewingTemplate.prompt && (
              <div>
                <label className="form-label font-medium">Prompt:</label>
                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                  <p className="whitespace-pre-wrap">
                    {viewingTemplate.prompt}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Template Confirmation Modal */}
      <Modal
        isOpen={isTemplateDeleteOpen}
        onClose={handleCancelDeleteTemplate}
        title="Delete Template"
      >
        <div>
          <p>Are you sure you want to delete this template?</p>
          <div className="w-full flex gap-2 justify-end py-4">
            <Button
              onClick={handleCancelDeleteTemplate}
              className="gray-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteTemplate}
              className="black-button"
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CommunityTriggerOptions;
